/* The Custom Location card's tour - see snippets/cf-custom-location-card.liquid for the why.
 *
 * One still per city (the plan half of a GRAND render) sits in the CUBE's window and is moved with
 * a CSS transform: it opens on the whole model, flies in to a stop, rests, flies on; above the box a
 * search field types each stop before the window goes there. After a city's last stop the next
 * search is typed over it, the window dips to black - the beat that says a new city is coming - and
 * the next model fades in whole.
 *
 * The timings are the ones signed off on the GIF previews of this card, ported one for one:
 *   fly in 1.5 s · rest 1.0 s · clear 0.08 s · type 28 ms a character · fly 0.75 s
 *   hand-over: clear, type over the old city, fade out 0.25 s, black 0.3 s, fade in 0.4 s
 *
 * Costs, because this is one card in a grid of 268:
 *   - Nothing runs until the card is on screen, and nothing runs while it is off it or the tab is
 *     hidden (the clock stops rather than skipping ahead, so it resumes where it was).
 *   - A frame writes a transform and an opacity to at most two <img>s; the browser composites them.
 *   - Stills are fetched at the width the window needs on this screen (a retina desktop card takes
 *     1400, a phone 1200), the first city at once, the others once the first has started.
 *   - prefers-reduced-motion: the script does nothing. The card image already shows the tour's first
 *     resting frame and the field its first search.
 */
(() => {
  if (customElements.get('cf-custom-location-card')) return;

  const FLYIN = 1.5, HOLD = 1.0, CLEAR = 0.08, TYPE_CH = 0.028, FLY = 0.75;
  const OUT = 0.25, BLACK = 0.3, IN = 0.4;

  const ease = (x) => {
    x = Math.max(0, Math.min(1, x));
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  };
  const typed = (term, k) => term.slice(0, Math.max(0, Math.min(term.length, Math.floor(k / TYPE_CH) + 1)));

  /* The loop as a list of segments, each a duration and a function of its own progress u (0..1)
     returning what the window shows: layers [{city, x, y, c, a}] (x, y the point in the middle of
     the window and c the width shown, all as fractions of the still; a the opacity), the field's
     text and its caret. */
  function build(cities, crop, flyin) {
    const seg = [];
    cities.forEach((c, i) => {
      const n = cities[(i + 1) % cities.length];
      const s0 = c.stops[0];
      const cr = c.crop || crop;        // a turned still (Paris) is narrower, so the same 500 px is more of its width
      seg.push([flyin, (u) => {
        const e = ease(u);
        const w = Math.exp(Math.log(c.full.c) + (Math.log(cr) - Math.log(c.full.c)) * e);
        return {
          layers: [{ city: c, x: c.full.x + (s0.x - c.full.x) * e, y: c.full.y + (s0.y - c.full.y) * e, c: w, a: 1 }],
          text: s0.term, caret: 'off',
        };
      }, { flyin: c }]);
      c.stops.forEach((s, j) => {
        const at = { city: c, x: s.x, y: s.y, c: cr, a: 1 };
        seg.push([HOLD, () => ({ layers: [at], text: s.term, caret: 'blink' })]);
        if (j < c.stops.length - 1) {
          const t = c.stops[j + 1];
          seg.push([CLEAR, () => ({ layers: [at], text: '', caret: 'on' })]);
          seg.push([t.term.length * TYPE_CH, (u) => ({ layers: [at], text: typed(t.term, u * t.term.length * TYPE_CH), caret: 'on' })]);
          seg.push([FLY, (u) => {
            const e = ease(u);
            return { layers: [{ city: c, x: s.x + (t.x - s.x) * e, y: s.y + (t.y - s.y) * e, c: cr, a: 1 }], text: t.term, caret: 'off' };
          }]);
        }
      });
      // the hand-over: typed over the old city, a moment of black, the next city whole
      const last = c.stops[c.stops.length - 1];
      const old = (a) => ({ city: c, x: last.x, y: last.y, c: cr, a });
      const term = n.stops[0].term;
      const typing = term.length * TYPE_CH;
      seg.push([CLEAR, () => ({ layers: [old(1)], text: '', caret: 'on', prime: n })]);
      seg.push([typing, (u) => ({ layers: [old(1)], text: typed(term, u * typing), caret: 'on' })]);
      seg.push([OUT, (u) => ({ layers: [old(1 - ease(u))], text: term, caret: 'on' })]);
      seg.push([BLACK, () => ({ layers: [], text: term, caret: 'on' })]);
      seg.push([IN, (u) => ({ layers: [{ city: n, x: n.full.x, y: n.full.y, c: n.full.c, a: ease(u) }], text: term, caret: 'off' }), { needs: n }]);
    });
    return seg;
  }

  class CfCustomLocationCard extends HTMLElement {
    connectedCallback() {
      /* Moved, not new: a card re-sorted into place (A-Z, price, the CUBE grid's late cards) is taken out of the page and
         put back. disconnectedCallback let go of the observers; take them up again, or the tour stands still for good. */
      if (this._started) {
        this.io.observe(this);
        this.ro.observe(this.win);
        document.addEventListener('visibilitychange', this.onVis);
        return;
      }
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let tour;
      try { tour = JSON.parse(this.querySelector('.cf-cl__tour').textContent); } catch (_) { return; }
      this._started = true;
      // Liquid's `t` escapes for HTML ("St Paul&#39;s"), and the field is written as text, so undo it once here
      const plain = document.createElement('textarea');
      tour.cities.forEach((c) => c.stops.forEach((s) => { plain.innerHTML = s.term; s.term = plain.value; }));

      this.win = this.querySelector('.cf-cl__window');
      this.textEl = this.querySelector('.cf-cl__text');
      this.fieldEl = this.querySelector('.cf-cl__field');
      this.caretEl = this.querySelector('.cf-cl__caret');
      this.crop = tour.crop;
      this.cities = tour.cities;
      // A tour whose whole model already fills the window (the FRAME's) has nothing to fly in to: flyin 0,
      // and a zero-length segment is never landed in.
      this.seg = build(this.cities, this.crop, tour.flyin === undefined ? FLYIN : tour.flyin);
      this.total = this.seg.reduce((sum, s) => sum + s[0], 0);
      this.t = this.seg[0][0];          // start resting on the first stop, the frame the card image already shows
      this.visible = false;

      this.measure();
      this.cities.forEach((c) => {
        const img = document.createElement('img');
        img.className = 'cf-cl__city';
        img.alt = '';
        img.decoding = 'async';
        c.img = img;
        c.ready = false;
        this.win.appendChild(img);
      });
      this.load(this.cities[0]).then(() => {
        this.win.classList.add('is-ready');
        this.cities.slice(1).forEach((c, k) => setTimeout(() => this.load(c), 1200 * (k + 1)));
      });

      this.io = new IntersectionObserver((entries) => {
        this.visible = entries.some((e) => e.isIntersecting);
        this.kick();
      }, { rootMargin: '100px' });
      this.io.observe(this);
      this.ro = new ResizeObserver(() => { this.measure(); this.draw(true); });
      this.ro.observe(this.win);
      this.onVis = () => this.kick();
      document.addEventListener('visibilitychange', this.onVis);
    }

    disconnectedCallback() {
      this.io && this.io.disconnect();
      this.ro && this.ro.disconnect();
      document.removeEventListener('visibilitychange', this.onVis);
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    /* The window's own size, not its box on screen: beside a GRAND a FRAME card is drawn at 80% of its tile
       (a transform, theme.css TRUE TO SIZE), and the still, inside that transform, is sized in the window's
       own pixels. A bounding box would be the shrunk one and the view would come out 80% too wide. */
    measure() {
      const cs = getComputedStyle(this.win);
      this.ww = parseFloat(cs.width);
      this.wh = parseFloat(cs.height);
    }

    /* The still's width on screen when a stop shows it 1:1, and the width to fetch it at for this screen. */
    base(c) {
      return this.ww / (c.crop || this.crop);
    }

    fetchWidth(c) {
      return Math.min(2048, Math.ceil((this.base(c) * (window.devicePixelRatio || 1)) / 200) * 200) || 1400;
    }

    /* A phone keeps only so many decoded images, and with a grid of 268 of them the next city may have
       been dropped by the time it is due - it would then come in late, a moment of dark window. So while
       its search is typed it is decoded again, and the fade-in waits for that, 0.7 s at most. */
    prime(c) {
      c.fresh = false;
      const done = () => { c.fresh = true; };
      Promise.race([c.img.decode ? c.img.decode().catch(() => {}) : Promise.resolve(), new Promise((r) => setTimeout(r, 700))]).then(done);
    }

    load(c) {
      if (c.loading) return c.loading;
      const url = new URL(c.src, location.href);
      url.searchParams.set('width', this.fetchWidth(c));
      c.img.src = url.toString();
      // Ready when decoded - or, since a browser may hold decode() back (a tab opened in the background
      // does until it is shown), shortly after the image has simply loaded; it then decodes on first paint.
      const loaded = new Promise((res) => {
        if (c.img.complete && c.img.naturalWidth) return res();
        c.img.addEventListener('load', res, { once: true });
        c.img.addEventListener('error', res, { once: true });
      });
      const decoded = c.img.decode ? c.img.decode().catch(() => {}) : loaded;
      c.loading = Promise.race([decoded, loaded.then(() => new Promise((r) => setTimeout(r, 300)))]).then(() => { c.ready = true; });
      return c.loading;
    }

    kick() {
      const run = this.visible && document.visibilityState === 'visible';
      if (run && !this.raf) {
        this.last = performance.now();
        this.raf = requestAnimationFrame((now) => this.tick(now));
      } else if (!run && this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }

    tick(now) {
      const dt = Math.min(0.1, (now - this.last) / 1000);
      this.last = now;
      if (this.cities[0].ready) {
        const next = (this.t + dt) % this.total;
        // a city whose still has not arrived yet: hold on the black until it has
        const s = this.segAt(next);
        const waitFor = s.meta && (s.meta.needs || s.meta.flyin);
        if (!waitFor || (waitFor.ready && waitFor.fresh !== false)) this.t = next;
        this.draw(false);
      }
      this.raf = requestAnimationFrame((n) => this.tick(n));
    }

    segAt(t) {
      for (const [d, fn, meta] of this.seg) {
        if (t < d) return { fn, u: t / d, meta };
        t -= d;
      }
      const [d, fn, meta] = this.seg[this.seg.length - 1];
      return { fn, u: 1, meta };
    }

    draw(force) {
      if (!this.cities[0].ready && !force) return;
      const { fn, u } = this.segAt(this.t);
      const st = fn(u);
      const shown = new Set();
      for (const l of st.layers) {
        const c = l.city;
        const z = (c.crop || this.crop) / l.c;
        const w = this.base(c), h = w * c.aspect;
        const tx = this.ww / 2 - l.x * w * z;
        const ty = this.wh / 2 - l.y * h * z;
        if (c.w !== w) { c.img.style.width = w + 'px'; c.w = w; }    // only when the card is resized: a width is a layout
        c.img.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0) scale(${z.toFixed(5)})`;
        c.img.style.opacity = l.a.toFixed(3);
        shown.add(c);
      }
      this.cities.forEach((c) => { if (!shown.has(c) && c.img.style.opacity !== '0') c.img.style.opacity = '0'; });

      if (st.prime && st.prime !== this.primedFor) {
        this.primedFor = st.prime;
        this.prime(st.prime);
      }
      if (st.text !== this.lastText) {
        this.textEl.textContent = st.text;
        this.fieldEl.scrollLeft = this.fieldEl.scrollWidth;    // a real input keeps the caret in view
        this.lastText = st.text;
      }
      if (st.caret !== this.lastCaret) {
        this.caretEl.dataset.caret = st.caret;
        this.lastCaret = st.caret;
      }
    }
  }

  customElements.define('cf-custom-location-card', CfCustomLocationCard);
})();
